<mat-drawer-container class="h-full w-full">
  <mat-drawer
    #drawer
    [mode]="(isMobile$ | async) ? 'over' : 'side'"
    [position]="(isMobile$ | async) ? 'end' : 'start'"
    [opened]="(isMobile$ | async) !== true"
    class="h-full w-full max-w-[90vw] sbnbDesktop:max-w-[30%]">
    <sbnb-chat-list
      [conversations$]="conversations$"
      [currentConversationUuid]="currentConversationUuid"
      [isMobile$]="isMobile$"
      (closeClicked)="close()"
      (conversationClicked)="onDrawerConversationSelected($event)"
      (newChatClicked)="onDrawerNewChatClicked()"
      (deleteClicked)="deleteConversation($event.event, $event.conversation)">
    </sbnb-chat-list>
  </mat-drawer>

  <div class="h-full w-full flex flex-col">
    <!-- Header Mobile -->
    <div *ngIf="isMobile$ | async" class="flex items-center justify-between p-4 flex-shrink-0">
      <!-- Left side - Close button -->
      <ui-button icon="griddy-chevron-left" variant="quaternary" size="sm" (onClick)="close()"></ui-button>

      <!-- Center - Title -->
      <span class="font-semibold">Copilot</span>

      <!-- Right side - Hamburger button -->
      <ui-button
        icon="griddy-menu"
        variant="quaternary"
        size="sm"
        class="opacity-100 sbnbDesktop:opacity-0"
        (onClick)="toggleDrawer()"
        data-testid="copilot-hamburger-button">
      </ui-button>
    </div>

    <!-- Content area with drawer -->
    <div class="copilot-container @container flex-1 flex overflow-hidden">
      <!-- Main Conversation Container -->
      <div class="copilot-content flex-1 flex flex-col min-w-0">
        <!-- Desktop Chat Header -->
        <div class="desktop-chat-header mb-4 hidden sbnbDesktop:block flex-shrink-0 px-4">
          <sbnb-cs-debug-item
            *ngIf="(currentConversation$ | async)?.uuid"
            label="copilot_conversation_uuid"
            [value]="(currentConversation$ | async)?.uuid"></sbnb-cs-debug-item>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoading" class="flex-1 flex justify-center items-center">
          <div class="flex items-center gap-2">
            <ui-loading-spinner [size]="16"></ui-loading-spinner>
            <span class="ml-2 text-gray-600">Loading conversation...</span>
          </div>
        </div>

        <!-- Conversation Content Container -->
        <div class="flex-1 flex flex-col min-h-0" *ngIf="!isLoading">
          <!-- Conversation History - Scrollable -->
          <div class="flex-1 overflow-y-auto px-4 min-h-0" data-testid="copilot-conversation" #conversationContainer>
            <div class="space-y-3 flex flex-col justify-end min-h-full px-2">
              <!-- Messages -->
              <ng-container *ngFor="let message of messages$ | async; trackBy: trackByMessageId; let isLast = last">
                <!-- User Message -->
                <sbnb-user-message
                  *ngIf="message.role === 'user'"
                  [message]="message"
                  [hasError]="hasMessageError(message.uuid)"
                  [progressMessage]="progressMessage$ | async"
                  (retryClick)="handleRetryClick($event)">
                </sbnb-user-message>

                <!-- Assistant Message -->
                <sbnb-assistant-message
                  *ngIf="message.role === 'assistant' && message.data && message.data.length > 0"
                  [message]="message"
                  [isLast]="isLast"
                  [requiresConfirmation]="(currentConversation$ | async)?.requires_confirmation"
                  [conversationUuid]="currentConversationUuid"
                  [feedback]="getMessageFeedback(message.uuid)"
                  (feedbackClick)="handleFeedbackClick($event)"
                  (confirmClick)="confirmAction()"
                  (cancelClick)="cancelAction()">
                </sbnb-assistant-message>
              </ng-container>

              <!-- Progress Message-->
              <div class="flex justify-start" *ngIf="progressMessage$ | async">
                <div class="py-2 mb-4 text-gray-600">
                  <div class="flex items-center gap-2">
                    <ui-loading-spinner [size]="16"></ui-loading-spinner>
                    <span>{{ streamingProgressMessage$ | async }}<span class="animated-dots"></span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Question Suggestions - At bottom when no conversation -->
          <div
            class="mt-auto px-4"
            *ngIf="showSuggestions && questionSuggestions.length > 0 && ((messages$ | async)?.length ?? 0) === 0">
            <div class="flex flex-col gap-4">
              <div class="flex flex-col gap-2 mr-2" data-testid="copilot-suggestions">
                <div class="flex flex-col gap-2 items-end">
                  <button
                    *ngFor="let suggestion of questionSuggestions"
                    (click)="selectSuggestion(suggestion)"
                    class="inline-flex px-4 py-2 text-sm rounded-lg border border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-colors text-gray-700 ring-1 ring-gray-300"
                    data-testid="copilot-suggestion-item">
                    <span class="whitespace-normal text-right">{{ suggestion }}</span>
                  </button>
                  <ui-button
                    trailingIcon="griddy-sync"
                    variant="quaternary"
                    size="sm"
                    label="Refresh suggestions"
                    (onClick)="loadQuestionSuggestions()"
                    class="text-raspberry-dark"
                    data-testid="refresh-suggestions-button">
                  </ui-button>
                </div>
              </div>
            </div>
          </div>

          <!-- Input Form - Sticky to bottom -->
          <div class="flex-shrink-0 bg-white">
            <!-- Unified input container with top border -->
            <div class="border-t border-gray-300 bg-white px-3">
              <div class="flex flex-col gap-3">
                <!-- Textarea without its own border -->
                <textarea
                  #questionTextarea
                  [formControl]="questionControl"
                  cdkTextareaAutosize
                  [cdkAutosizeMinRows]="3"
                  [cdkAutosizeMaxRows]="12"
                  class="w-full p-0 pt-1 border-0 bg-transparent leading-normal resize-none focus:outline-none transition-[height] duration-200 ease-out overflow-y-auto"
                  [class.text-rose-darker]="questionControl.touched && questionControl.invalid"
                  placeholder="Type your question here..."
                  (keydown)="onKeyDown($event)"
                  (focus)="onTextareaFocus()"
                  data-testid="copilot-question-input"></textarea>

                <!-- Action buttons row inside the bordered container -->
                <div class="flex justify-between items-center lg:pb-1 lg:pe-1">
                  <!-- Left side - error message -->
                  <div class="flex-1">
                    <div
                      *ngIf="questionControl.touched && questionControl.errors?.['minlength']"
                      class="text-rose-darker text-sm"
                      data-testid="copilot-input-error">
                      Please enter at least one character
                    </div>
                  </div>

                  <!-- Right side - action buttons -->
                  <div class="flex items-center gap-2 mb-2">
                    <sbnb-voice-to-text
                      *ngIf="voiceSessionId() && !isCurrentConversationSubmitting"
                      [sessionId]="voiceSessionId()"
                      [enableDirectFeedback]="true"></sbnb-voice-to-text>

                    <ui-button
                      label="Ask"
                      variant="primary"
                      size="sm"
                      trailingIcon="griddy-arrow-up"
                      [disabled]="questionControl.invalid || isCurrentConversationSubmitting"
                      (onClick)="onSubmit(null)"
                      data-testid="copilot-submit-button"></ui-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</mat-drawer-container>
